import styled, { css } from 'styled-components';
import moment from 'moment';
import { IConversationThreadFieldsFragment } from '../../../utils/apollo/graphqlSchema';
import { assertNotNil } from '../../../utils/types';
import { theme } from '../../../styles/theme';
import { SvgIcon } from '../../Icon';
import Avatar from '../../Avatar';
import ReadOnlyConversationMessage from '../ReadOnlyConversationMessage';
import ConversationThreadHeading from './ConversationThreadHeading';
import ConversationThreadSummary from './ConversationThreadSummary';

interface IProps {
  conversationThread: IConversationThreadFieldsFragment;
  onClick: (conversationThread: IConversationThreadFieldsFragment) => void;
  isActive?: boolean;
}

const Container = styled.div<{ $isActive?: boolean }>`
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  row-gap: 4px;
  padding: 8px 16px;
  transition: background-color 0.2s ease-in-out;
  &:hover {
    cursor: pointer;
    background-color: ${theme.colors.blue500};
  }
  ${({ $isActive }) =>
    $isActive &&
    css`
      background-color: ${theme.colors.blue500};
    `}
`;
const HorizontalRow = styled.div`
  display: flex;
  align-items: center;
  column-gap: 4px;
  min-height: 16px;
`;

const CreatorName = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${theme.colors.secondary900};
`;
const CreatedAtValue = styled.div`
  font-size: 12px;
  font-weight: 400;
  color: ${theme.colors.secondary800};
`;

const ConversationThread = ({
  conversationThread,
  onClick,
  isActive,
}: IProps) => {
  const { comments, entity, hasUnreadComments, creator, createdAt } =
    conversationThread;

  const startingComment = comments[0];
  assertNotNil(startingComment);
  const { message } = startingComment;

  const handleClick = () => {
    if (!isActive) {
      onClick(conversationThread);
    }
  };

  return (
    <Container onClick={handleClick} $isActive={isActive}>
      <HorizontalRow>
        <ConversationThreadHeading entity={entity} />
        {hasUnreadComments && (
          <SvgIcon
            name="dot-outline-fill"
            width={16}
            height={16}
            customFill={theme.colors.mainBlue}
          />
        )}
      </HorizontalRow>
      <HorizontalRow>
        <Avatar picture={creator.picture} size={16} />
        <CreatorName>{creator.name}</CreatorName>
        <CreatedAtValue>{moment.utc(createdAt).fromNow()}</CreatedAtValue>
      </HorizontalRow>
      {message?.html && (
        <ReadOnlyConversationMessage key={message.id} content={message.html} />
      )}
      <ConversationThreadSummary comments={comments} />
    </Container>
  );
};

export default ConversationThread;
